import type {
    CoBuyer,
    CommonConsumer,
    DXGAddress,
    DXGGlobalParticipant,
    DXGParticipantEmployment,
    IShopper,
    MaritalStatus
} from '@makemydeal/dr-platform-types';
import { COMMON_CONSUMER_REQUIRED_FIELDS } from '@makemydeal/dr-sales-checkin-types';
import { isEmpty, replace } from 'lodash';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { HostEnvironment } from '@makemydeal/dr-shared-ui-utils';
import { resolveCellPhone } from '@makemydeal/dr-shared-ui-utils/phone/resolvePhone';
import { utils as offerReduxUtils } from '@makemydeal/dr-offer-redux';
import type { QualifiedPhoneNumbers } from '@makemydeal/dr-shared-types';
import type { Participant, SCICommonConsumer } from '@makemydeal/dr-sales-checkin-types';
import { selectEnv } from '../environment/environmentUtils';
import type { BusinessShopperFields, ParticipantShopperFields, SeededShopper } from './types';

export const isValidCustomer = (customer: CommonConsumer) =>
    COMMON_CONSUMER_REQUIRED_FIELDS.every((field: keyof CommonConsumer) => {
        const value = customer[field];
        return typeof value === 'string' && !isEmpty(value);
    });

export function useQuery() {
    const { search } = useLocation();
    return useMemo(() => new URLSearchParams(search), [search]);
}

export const getElapsedTime = (utcTimestamp: string): string => {
    const createdDate = new Date(utcTimestamp); // Parse the UTC timestamp
    const now = new Date(); // Current local time
    const diffInSeconds = Math.floor((now.getTime() - createdDate.getTime()) / 1000);

    if (diffInSeconds < 60) {
        return diffInSeconds === 1 ? '1 second ago' : `${diffInSeconds} seconds ago`;
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
        return diffInMinutes === 1 ? '1 minute ago' : `${diffInMinutes} minutes ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
        return diffInHours === 1 ? '1 hour ago' : `${diffInHours} hours ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    return diffInDays === 1 ? '1 day ago' : `${diffInDays} days ago`;
};

export function getDealerLogoBaseUrl(dnSalesEnv: string | HostEnvironment) {
    let baseUrl = '';
    switch (dnSalesEnv) {
        case 'local':
        case 'dev2':
        case 'dev':
        case '':
            baseUrl = 'https://dev.accelerate.dealer.com/static/img/dealer_logo';
            break;
        case 'uat':
            // UAT doesn't have logos, fall back to QA
            baseUrl = 'https://qa.accelerate.dealer.com/static/img/dealer_logo';
            break;
        case 'qa': {
            baseUrl = 'https://qa.accelerate.dealer.com/static/img/dealer_logo';
            break;
        }
        case 'production':
        case 'prod': {
            baseUrl = 'https://accelerate.dealer.com/static/img/dealer_logo';
            break;
        }
        default:
            baseUrl = 'https://dev.accelerate.dealer.com/static/img/dealer_logo';
    }
    return baseUrl;
}

/**
 * Get the secure accelerate dealer URL based on environment
 * Used for credit app and other secure dealer services
 * Example (qa): https://secure-qa.accelerate.dealer.com/dealer
 * Example (prod): https://secure.accelerate.dealer.com/dealer
 *
 * @param env - The environment name (e.g., 'local', 'dev', 'qa', 'prod', 'production')
 * @returns The secure accelerate dealer URL for the given environment
 */
export function getSecureAccelerateDealerUrl(env: string): string {
    const envVar = selectEnv(env, 'VAR');
    const hyphenSuffix = envVar ? `-${envVar}` : '';
    return `https://secure${hyphenSuffix}.accelerate.dealer.com/dealer`;
}

// TODO: Need to remove and use environmentNameUtils.getEnvironmentName(env)
export const getEnvironmentName = (env: HostEnvironment): string => {
    switch (env) {
        case HostEnvironment.Local:
            return 'Local';
        case HostEnvironment.Dev:
            return 'Dev';
        case HostEnvironment.Dev2:
            return 'Dev2';
        case HostEnvironment.QA:
            return 'QA';
        case HostEnvironment.UAT:
            return 'UAT';
        case HostEnvironment.DarkProd:
            return 'DarkProd';
        case HostEnvironment.Prod:
            return 'Prod';
        case HostEnvironment.Unknown:
            return 'Unknown';
        case HostEnvironment.None:
            return 'None';
        default:
            return 'Invalid';
    }
};

const resolveConsumerPhones = (buyer: Participant, globalParticipant?: DXGGlobalParticipant | null): QualifiedPhoneNumbers => {
    const qualified = offerReduxUtils.getQualifiedPhoneNumbers(globalParticipant);

    return {
        ...qualified,
        cellPhone: resolveCellPhone({ ...qualified, phone: buyer.phoneNumber })
    };
};

/**
 * Converts the Participant object into Consumer object
 */
export function composeConsumer(
    buyer: Participant,
    commonOrgId: string,
    globalParticipant?: DXGGlobalParticipant | null,
    enableCorrectPhoneHandling = false
): SCICommonConsumer {
    const isBusiness = 'legalName' in buyer;
    const signer = isBusiness ? buyer.businessSigner : buyer;
    const title = isBusiness ? buyer.businessSigner?.title ?? buyer.businessSigner?.salutation : buyer.salutation;

    const phones = enableCorrectPhoneHandling
        ? resolveConsumerPhones(buyer, globalParticipant)
        : { cellPhone: buyer.phoneNumber ?? '' };

    return {
        consumerId: buyer.commonConsumerId,
        dealerGroupId: '',
        dealershipId: commonOrgId,
        firstName: signer?.firstName ?? '',
        middleName: signer?.middleName ?? '',
        title: title ?? '',
        lastName: signer?.lastName ?? '',
        primaryEmail: buyer.emailAddress,
        postalCode: buyer.address?.postalCode ?? buyer.postalCode ?? '',
        suffix: signer?.suffix ?? '',
        city: buyer.address?.city ?? '',
        stateOrProvince: buyer.address?.stateProvince ?? '',
        countyOrRegion: buyer.address?.countyDistrict ?? '',
        ...phones,
        street1: buyer.address?.addressLine1 ?? '',
        street2: buyer.address?.addressLine2 ?? '',
        country: buyer.address?.country ?? '',
        legalName: buyer.legalName ?? '',
        business: {
            businessEntityId: buyer.businessEntityId ?? '',
            legalName: buyer.legalName ?? ''
        },
        relationshipToBuyer: buyer.relationshipToBuyer ?? 'Other',
        participantRole: buyer.participantRole as CoBuyer['participantRole']
    };
}

export const formatCustomerName = (firstName: string, lastName: string) => {
    return `${firstName || ''} ${lastName || ''}`;
};

export const sanitizePostalCode = (postalCode: string): string => {
    return replace(postalCode, /-/g, '');
};

export const sanitizePhoneNumber = (phone: string | undefined): string => {
    if (!phone) return '';
    return phone.replace(/[-\s()]/g, '');
};

export const mapSelectedCustomer = (customer: CommonConsumer) => {
    return {
        ...customer,
        stateOrProvince: customer.stateOrProvinceAbbreviation || customer.stateOrProvince
    };
};

export const mapCommonConsumerToShopper = (customer?: CommonConsumer | null, customerType?: string): SeededShopper => {
    const phone = [customer?.cellPhone, customer?.homePhone, customer?.workPhone].find((p) => Boolean(p)) ?? '';

    return {
        contactPreferences: {},
        commonConsumerId: customer?.consumerId,
        firstName: customer?.firstName ?? '',
        middleName: customer?.middleName,
        lastName: customer?.lastName ?? '',
        title: customer?.title,
        suffix: customer?.suffix,
        email: customer?.primaryEmail?.toLowerCase() ?? '',
        emailAlternative: customer?.alternateEmail?.toLowerCase(),
        phone,
        cellPhone: customer?.cellPhone,
        homePhone: customer?.homePhone,
        workPhone: customer?.workPhone,
        streetAddress: customer?.street1,
        streetAddress2: customer?.street2,
        city: customer?.city,
        state: customer?.stateOrProvinceAbbreviation || customer?.stateOrProvince,
        county: customer?.countyOrRegion,
        zip: sanitizePostalCode(customer?.postalCode ?? '').substring(0, 5),
        ...(customerType && { customerType: customerType as IShopper['customerType'] }),
        ...(customerType === 'Business' && customer?.business && { business: customer.business }),
        ...(customer?.maritalStatus && { maritalStatus: customer.maritalStatus as MaritalStatus }),
        // Co-buyer role fields (Co-Applicant Type / Relationship to Buyer). Absent on buyers.
        ...(customer?.participantRole && { participantRole: customer.participantRole }),
        ...(customer?.relationshipToBuyer && {
            relationshipToBuyer: customer.relationshipToBuyer as CoBuyer['relationshipToBuyer']
        })
    };
};

export const mapParticipantToShopperFields = (
    participant?: Pick<
        DXGGlobalParticipant,
        'phoneContactInfo' | 'additionalContacts' | 'additionalAddresses' | 'maritalStatus'
    > | null,
    enableCorrectPhoneHandling = false
): ParticipantShopperFields => {
    if (!participant) return {};

    const pci = participant.phoneContactInfo;
    let homePhone = !enableCorrectPhoneHandling && pci?.contactType === 'Personal' && pci.isMobile === false ? pci.phoneNumber : '';
    let workPhone = !enableCorrectPhoneHandling && pci?.contactType === 'Work' ? pci.phoneNumber : '';
    let emailAlternative: string | undefined;

    participant.additionalContacts?.forEach((contact) => {
        if ('phoneNumber' in contact) {
            if (enableCorrectPhoneHandling) return;
            if (contact.contactType === 'Personal' && contact.isMobile === false && contact.phoneNumber)
                homePhone = contact.phoneNumber;
            if (contact.contactType === 'Other' && contact.isMobile === false && contact.phoneNumber && !homePhone)
                homePhone = contact.phoneNumber;
            if (contact.contactType === 'Work' && contact.phoneNumber) workPhone = contact.phoneNumber;
        } else if (!emailAlternative) {
            emailAlternative = contact.emailAddress;
        }
    });

    const billing = participant.additionalAddresses?.find((address) => address.addressType === 'Billing');
    const billingAddress = billing && {
        ...(billing.addressLine1 && { streetAddress: billing.addressLine1 }),
        ...(billing.addressLine2 && { streetAddress2: billing.addressLine2 }),
        ...(billing.city && { city: billing.city }),
        ...(billing.stateProvince && { state: billing.stateProvince }),
        ...(billing.countyDistrict && { county: billing.countyDistrict }),
        ...(billing.country && { country: billing.country }),
        zip: billing.postalCode ?? ''
    };

    return {
        ...(homePhone && { homePhone }),
        ...(workPhone && { workPhone }),
        ...(emailAlternative && { emailAlternative: emailAlternative.toLowerCase() }),
        ...(billingAddress && { billingAddress }),
        ...(participant.maritalStatus && { maritalStatus: participant.maritalStatus })
    };
};

// Mirrors Acceleron mapToShopper: DXG stores tenure as TOTAL months; the card renders split years + months.
const splitMonthsToYearsAndMonths = (totalMonths?: number): { years?: number; months?: number } =>
    typeof totalMonths === 'number' ? { years: Math.floor(totalMonths / 12), months: totalMonths % 12 } : {};

const mapDxgAddress = (address?: DXGAddress | null): IShopper['previousAddress'] => {
    if (!address) return undefined;
    const mapped: NonNullable<IShopper['previousAddress']> = {
        ...(address.addressLine1 && { streetAddress: address.addressLine1 }),
        ...(address.addressLine2 && { streetAddress2: address.addressLine2 }),
        ...(address.city && { city: address.city }),
        ...(address.stateProvince && { state: address.stateProvince }),
        ...(address.postalCode && { zip: address.postalCode }),
        ...(address.countyDistrict && { county: address.countyDistrict })
    };
    return Object.keys(mapped).length > 0 ? mapped : undefined;
};

const mapEmployment = (employment?: DXGParticipantEmployment | null): IShopper['currentEmployment'] => {
    if (!employment) return undefined;
    const { years: totalYearsEmployed, months: totalMonthsEmployed } = splitMonthsToYearsAndMonths(employment.totalMonthsEmployed);
    const mapped = {
        ...(employment.status && { status: employment.status }),
        ...(employment.employerName && { employerName: employment.employerName }),
        ...(employment.occupation && { occupation: employment.occupation }),
        ...(employment.workPhone && { workPhone: employment.workPhone }),
        ...(totalMonthsEmployed !== undefined && { totalMonthsEmployed }),
        ...(totalYearsEmployed !== undefined && { totalYearsEmployed })
    } as NonNullable<IShopper['currentEmployment']>;
    return Object.keys(mapped).length > 0 ? mapped : undefined;
};

// Mirrors Acceleron mapToShopper's housing/employment/financial mapping. SCI seeds from the DXG
// global participant (it has no Acceleron init), so it must do the total-months split itself.
export const mapCreditAppFields = (participant?: DXGGlobalParticipant | null): Partial<IShopper> => {
    if (!participant) return {};

    const housingInfo = participant.housingInfo;
    const hasHousingInfo = Boolean(housingInfo?.housingStatus) || housingInfo?.mortgageOrRentAmount !== undefined;

    const { years: yearsAtCurrentAddress, months: monthsAtCurrentAddress } = splitMonthsToYearsAndMonths(
        participant.primaryAddress?.monthsAtCurrentAddress
    );
    const { years: yearsAtPreviousAddress, months: monthsAtPreviousAddress } = splitMonthsToYearsAndMonths(
        participant.previousAddress?.monthsAtPreviousAddress
    );
    const previousAddress = mapDxgAddress(participant.previousAddress);

    const currentEmployment = mapEmployment(participant.employmentInfo?.currentEmployment);
    const employerAddress = mapDxgAddress(participant.employmentInfo?.currentEmployment?.employerAddress);
    const currentEmploymentWithAddress =
        currentEmployment || employerAddress
            ? ({ ...currentEmployment, ...(employerAddress && { address: employerAddress }) } as NonNullable<
                  IShopper['currentEmployment']
              >)
            : undefined;
    const previousEmployment = mapEmployment(participant.employmentInfo?.previousEmployment);

    const financialInfo = participant.financialInfo;
    const mappedFinancialInfo = {
        ...(financialInfo?.incomeFrequency && { incomeFrequency: financialInfo.incomeFrequency }),
        ...(financialInfo?.income !== undefined && { income: financialInfo.income }),
        ...(financialInfo?.otherIncomeSource && { otherIncomeSource: financialInfo.otherIncomeSource }),
        ...(financialInfo?.otherIncome !== undefined && { otherIncome: financialInfo.otherIncome })
    } as NonNullable<IShopper['financialInfo']>;

    return {
        ...(hasHousingInfo && { housingInfo: housingInfo as IShopper['housingInfo'] }),
        ...(monthsAtCurrentAddress !== undefined && { monthsAtCurrentAddress }),
        ...(yearsAtCurrentAddress !== undefined && { yearsAtCurrentAddress }),
        ...(previousAddress && { previousAddress }),
        ...(monthsAtPreviousAddress !== undefined && { monthsAtPreviousAddress }),
        ...(yearsAtPreviousAddress !== undefined && { yearsAtPreviousAddress }),
        ...(currentEmploymentWithAddress && { currentEmployment: currentEmploymentWithAddress }),
        ...(previousEmployment && { previousEmployment }),
        ...(Object.keys(mappedFinancialInfo).length > 0 && { financialInfo: mappedFinancialInfo }),
        ...(participant.creditPullConsentGiven !== undefined && {
            creditPullConsentGiven: participant.creditPullConsentGiven
        })
    };
};

export const mapBusinessFields = (
    participant: Participant,
    globalParticipant?: DXGGlobalParticipant | null
): BusinessShopperFields => {
    if (globalParticipant?.customerType !== 'Business') return {};

    const business = globalParticipant.business;

    return {
        business: {
            legalName: participant.legalName ?? '',
            businessEntityId: participant.businessEntityId ?? '',
            ...(participant.taxIdNumber && { taxIdNumber: participant.taxIdNumber }),
            ...(business?.businessType && { businessType: business.businessType }),
            ...(business?.businessEstablishedDate && { businessEstablishedDate: business.businessEstablishedDate }),
            ...(business?.numberOfEmployees !== undefined && { numberOfEmployees: business.numberOfEmployees }),
            ...(business?.stateOfIncorporation && { stateOfIncorporation: business.stateOfIncorporation }),
            ...(business?.financialStatementDescription && {
                financialStatementDescription: business.financialStatementDescription
            }),
            ...(business?.bankName && { bankName: business.bankName }),
            ...(business?.bankContactName?.firstName && { bankContactName: business.bankContactName.firstName }),
            ...(business?.bankContactPhoneNumber?.phoneNumber && {
                bankContactPhoneNumber: business.bankContactPhoneNumber.phoneNumber
            }),
            ...(business?.businessSensitivePersonalData?.bankAccountNumber && {
                bankAccountNumber: business.businessSensitivePersonalData.bankAccountNumber
            })
        }
    };
};

export const composeShopper = (
    participant: Participant,
    commonOrgId: string,
    globalParticipant?: DXGGlobalParticipant | null,
    enableCorrectPhoneHandling = false
): SeededShopper => ({
    ...mapCommonConsumerToShopper(
        composeConsumer(participant, commonOrgId, globalParticipant, enableCorrectPhoneHandling),
        globalParticipant?.customerType
    ),
    ...mapParticipantToShopperFields(globalParticipant, enableCorrectPhoneHandling),
    ...mapCreditAppFields(globalParticipant),
    ...mapBusinessFields(participant, globalParticipant),
    ...(enableCorrectPhoneHandling && { phone: participant.phoneNumber ?? '' })
});
